import { ContentAttached, ContentDetach, DocumentRecordPerson } from '@fs/zion-icon'
import { colors } from '@fs/zion-ui'

/**
 * Determines which icon to display based on attachment status
 *
 * @param {string|null} attachmentId - The ID of the attachment, if one exists
 * @param {string|null} matchId - The ID of a potential match, if one exists
 * @returns {Function} - Returns the appropriate icon component based on status
 */
export const getIconByAttachmentStatus = (attachmentId, matchId) => {
  if (attachmentId) return ContentAttached
  if (matchId) return DocumentRecordPerson
  return ContentDetach
}

/**
 * Returns the background color for the icon based on attachment status
 *
 * @param {string|null} attachmentId - The ID of the attachment, if one exists
 * @param {string|null} matchId - The ID of a potential match, if one exists
 * @returns {string} - The background color for the icon
 */
export const getIconBackgroundColor = (attachmentId, matchId) => {
  if (attachmentId) return colors.green10
  if (matchId) return colors.blue10
  return colors.gray10
}

/**
 * Generates a test ID for the icon element
 *
 * @param {string|null} attachmentId - The ID of the attachment, if one exists
 * @param {string|null} matchId - The ID of a potential match, if one exists
 * @returns {string} - A formatted test ID string for the icon element
 */
export const getIconTestId = (attachmentId, matchId) => {
  let iconName = 'ContentDetach'
  if (attachmentId) iconName = 'ContentAttached'
  else if (matchId) iconName = 'DocumentRecordPerson'
  const id = attachmentId || matchId || 'none'
  return `source-linker-button-${id}-${iconName}`
}

/**
 * Returns the attachment status text for display as a subheading
 *
 * @param {string|object|null} attachmentId - The ID of the attachment, if one exists
 * @param {string|object|null} matchId - The ID of a potential match, if one exists
 * @param {Function} t - Translation function
 * @returns {string} - The attachment status text
 */
export const getAttachmentStatusText = (attachmentId, matchId, t) => {
  if (attachmentId) {
    // Handle both string and object attachment IDs
    let shortId = attachmentId
    if (typeof attachmentId === 'string' && attachmentId.includes('/')) {
      shortId = attachmentId.split('/').pop() || attachmentId
    } else if (typeof attachmentId === 'object' && attachmentId !== null) {
      // If it's an object, try to extract a meaningful ID
      shortId = attachmentId.id || attachmentId.personId || 'Tree'
    }
    return t('select-person.attachment-attached', 'Attached to {pid}', { pid: shortId })
  }
  if (matchId) {
    return t('select-person.attachment-possible-match', 'Possible Match')
  }
  return t('select-person.attachment-none', 'No Attachment')
}
